<template>
  <FlotoContentLoader :loading="loading">
    <FlotoFixedView v-if="approver && approver.id">
      <span v-if="!detailComponent">
        {{ $tc(parentModuleName) }} details component missing !!
      </span>
      <FormRulesProvider
        :key="parentModuleName"
        :module-name="parentModuleName"
      >
        <component
          :is="detailComponent"
          :back-link="backLink || $currentModule.getRoute()"
          :module-name="parentModuleName"
          :resource-id="approver.parentResourceId"
          :approval="approver"
          disabled
          is-page
          :approval-ask-on-status-id="approver.approvalAskOnStatusId"
          is-approval
          @resource="resource = $event"
        >
          <template v-slot:actions>
            <MRow>
              <MCol :size="12" class="flex">
                <ApprovalDecisionActions
                  ref="listRef"
                  :status="approver.status"
                  :approval-settings="approvalSettings"
                  :approver="approver"
                  @update="handleStatusChange"
                  @refresh="handleRefresh"
                >
                  <template v-slot:delegator-info>
                    <span
                      v-if="
                        user.delegatorApproval &&
                        user.delegatorApproval.indexOf(approver.userId) >= 0 &&
                        approver.status === 'pending'
                      "
                      style="
                        position: absolute;
                        right: 4px;
                        bottom: -22px;
                        font-size: 12px;
                      "
                    >
                      <span class="text-neutral-light">{{
                        `${$tc('on')} ${$tc('behalf')} ${$tc('of')}`
                      }}</span>
                      <FlotoUserDrawer :user="technician(approver.userId)" />
                    </span>
                  </template>
                </ApprovalDecisionActions>
                <KnowledgeApprovalContainer
                  v-if="isKnowledgeDetail"
                  :resource-id="approver.parentResourceId"
                />
              </MCol>
            </MRow>
          </template>
          <template v-slot:comments>
            <MCol
              v-if="isKnowledgeDetail && resource.status !== 'expired'"
              v-bind="sidebarWidth"
              style="min-height: 120px"
            >
              <h6 class="text-primary font-normal">{{ $tc('comment', 2) }}</h6>
              <MDivider />
              <ConversationContainer
                :key="conversationRenderCount"
                :module-name="$constants.APPROVAL_STAGE"
                :resource-id="approver.stageId"
              />
            </MCol>
          </template>
          <!-- default content as tabs -->
          <template v-slot="{ ticket }">
            <MDivider />
            <MAffix>
              <MTab v-model="activeTabKey" size="small" class="affixed-tab">
                <MTabPane key="work" :tab="$tc('comment', 2)" />
                <MTabPane
                  v-if="parentModuleName === $constants.PROBLEM"
                  key="analysis"
                  :tab="$tc('analysis', 2)"
                />
                <MTabPane
                  v-if="
                    parentModuleName === $constants.CHANGE ||
                    parentModuleName === $constants.RELEASE
                  "
                  key="planning"
                  :tab="$tc('planning', 2)"
                />
                <MTabPane
                  v-if="isTicket && ticket.serviceCatalogId"
                  key="request_information"
                  :tab="`${$tc('request')} ${$t('information')}`"
                />
                <MTabPane
                  v-if="isAsset && isConstomFieldAvailable(ticket)"
                  key="custom_field"
                  :tab="`${$tc('custom')} ${$tc('field', 2)}`"
                />
                <MTabPane
                  v-if="parentModuleName === $constants.PURCHASE"
                  key="purchase_detail"
                  :tab="`${$tc('purchase')} ${$tc('details')}`,"
                />
                <MTabPane key="approval" :tab="$tc('approval')" />
                <MTabPane
                  v-if="parentModuleName === $constants.ASSET_MOVEMENT"
                  key="asset"
                  :tab="`${$tc('asset')}`,"
                />
                <MTabPane
                  v-if="
                    isTicket ||
                    isAsset ||
                    parentModuleName === $constants.PURCHASE
                  "
                  key="relation"
                  :tab="`${
                    isAsset
                      ? `${$tc('linked_event', 2)}`
                      : `${$tc('relation', 2)}`
                  } (${relationCount})`"
                />
                <MTabPane
                  v-if="isAsset && approver.makerCheckerApproval"
                  key="asset_update"
                  :tab="`${$tc('asset_update')}`,"
                />
              </MTab>
            </MAffix>
            <div class="my-4 mr-6">
              <CustomFieldTab
                v-if="isAsset && activeTabKey === 'custom_field'"
                key="custom_field"
                :asset="ticket"
                :custom-fields-for-all="customFieldsForAll"
                :selected-asset-types="selectedAssetTypes(ticket)"
                :custom-field-for-asset-type="customFieldForAssetType(ticket)"
                :asset-type-id="
                  ticket[
                    `${customFieldAttributesTypePrefix(parentModuleName)}TypeId`
                  ]
                "
                :update-api="(e) => e"
                :disabled="true"
              />
              <ConversationContainer
                v-if="activeTabKey === 'work'"
                :key="conversationRenderCount"
                :module-name="$constants.APPROVAL_STAGE"
                :resource-id="approver.stageId"
                :disabled="approver.archived"
              />
              <RequestInformationTab
                v-if="isTicket && activeTabKey === 'request_information'"
                :resource="ticket"
                disabled
                is-approval
              />
              <ProblemAnalysisTab
                v-if="isTicket && activeTabKey === 'analysis'"
                :resource="ticket"
                disabled
                hide-search-bar
              />
              <ChangeActivityTab
                v-if="isTicket && activeTabKey === 'planning'"
                :resource="ticket"
                disabled
                :change-stage="changeStage"
                :module-name="parentModuleName"
              />
              <PurchaseDetailTab
                v-if="
                  parentModuleName === $constants.PURCHASE &&
                  activeTabKey === 'purchase_detail'
                "
                :resource="ticket"
                :price-custom-fields="priceCustomFields"
                disabled
              />
              <AssetTab
                v-if="
                  parentModuleName === $constants.ASSET_MOVEMENT &&
                  activeTabKey === 'asset'
                "
                :resource="ticket"
                :resource-id="ticket.id"
                disabled
                is-approval
              />
              <ApprovalContainer
                v-if="activeTabKey === 'approval'"
                :module-name="parentModuleName"
                :resource-id="
                  parentModuleName === $constants.ASSET_MOVEMENT
                    ? approver.parentResourceId
                    : resource.id
                "
                disabled
              />
              <RelationContainer
                v-if="activeTabKey === 'relation'"
                :module-name="
                  isAsset && parentModuleName !== $constants.CMDB
                    ? $constants.ASSET
                    : parentModuleName
                "
                :resource="ticket"
                disabled
                :resource-id="ticket.id"
                is-approval
                :sub-module-name="isAsset ? parentModuleName : undefined"
                @relation-added="handleRelationCount"
                @relation-removed="handleRelationRemoved"
                @refresh="handleRefresh"
              />
              <ApprovalAssetUpdateData
                v-if="activeTabKey === 'asset_update'"
                :stage-id="approver.approvalId"
              />
            </div>
          </template>
          <!-- <div class="my-4">
          <ConversationContainer
            :key="conversationRenderCount"
            :module-name="$constants.APPROVAL_STAGE"
            :resource-id="approver.stageId"
          />
        </div> -->
        </component>
      </FormRulesProvider>
    </FlotoFixedView>
    <NoteRules ref="noteDrawer" :create-fn="handleNoteAdded" />
  </FlotoContentLoader>
</template>

<script>
import FormRulesProvider from '@components/providers/form-rules-provider/form-rules-provider'
import TicketDetailView from '@modules/ticket/components/detail'
import PurchaseDetail from '@modules/purchase/components/detail'
import Read from '@modules/knowledge/views/read'
import AssetMovementDetail from '@modules/asset-management/components/asset-movement-detail'
import AssetDetailView from '@modules/asset/components/detail'
import { updateApproverApi } from '@components/approval/approval-api'
import ConversationContainer from '@components/conversation/conversation-container'
import { authComputed } from '@state/modules/auth'
import { getApprovalSettingApi } from '@modules/approval/approval-api'
import RequestInformationTab from '@modules/ticket/components/request-information-tab'
import ProblemAnalysisTab from '@modules/ticket/components/problem-analysis-tab'
import ChangeActivityTab from '@modules/ticket/components/change-activity-tab'
import PurchaseDetailTab from '@modules/purchase/components/purchase-detail-tab'
import AssetTab from '@modules/asset-management/components/asset-movement/asset-list'
import NoteRules from '@components/note-rules/note-rules'
import { FormComputed } from '@state/modules/form'
import { AssetTypeComputed } from '@state/modules/asset-type'
import { CITypeComputed } from '@state/modules/ci-type'
import { findValuePathWithItems } from '@data/recursive'
import {
  customFieldsForAllAssetType,
  customFieldAttributesTypePrefix,
  customFieldAttributesModuleName,
} from '@modules/asset/helpers/asset-custom-fields'
import CustomFieldTab from '@modules/asset/components/property/custom-field-tab'
import { getApproverApi } from '../my-approval-api'
import ApprovalDecisionActions from '../components/approval-decision-action'
import ApprovalAssetUpdateData from '@components/approval/components/approval-asset-update-data'
import { TechnicianComputed } from '@state/modules/technician'
import RelationContainer from '@components/relation/relation-container'
import { getRelationCountApi } from '@components/relation/relation-api'
import ApprovalContainer from '@components/approval/approval-container'
import KnowledgeApprovalContainer from '../components/knowledge-approval-container'

export default {
  name: 'DetailView',
  components: {
    FormRulesProvider,
    TicketDetailView,
    PurchaseDetail,
    AssetDetailView,
    AssetMovementDetail,
    ApprovalDecisionActions,
    ConversationContainer,
    NoteRules,
    RequestInformationTab,
    CustomFieldTab,
    Read,
    PurchaseDetailTab,
    ProblemAnalysisTab,
    ChangeActivityTab,
    AssetTab,
    RelationContainer,
    ApprovalAssetUpdateData,
    ApprovalContainer,
    KnowledgeApprovalContainer,
  },
  props: {
    backLink: { type: Object, default: undefined },
    sidebarWidth: { type: Object, default: () => ({ size: 12 }) },
  },
  data() {
    this.customFieldAttributesTypePrefix = customFieldAttributesTypePrefix
    return {
      loading: true,
      approver: {},
      approvalSettings: {},
      pendingChanges: null,
      conversationRenderCount: 1,
      activeTabKey: 'work',
      changeStage: undefined,
      relationCount: 0,
      resource: {},
    }
  },
  computed: {
    ...authComputed,
    ...TechnicianComputed,
    isTicket() {
      if (
        [
          this.$constants.REQUEST,
          this.$constants.PROBLEM,
          this.$constants.CHANGE,
          this.$constants.RELEASE,
        ].indexOf(this.approver.parentResource) >= 0
      ) {
        return true
      }
      return false
    },
    isAsset() {
      if (
        [
          this.$constants.ASSET,
          this.$constants.ASSET_HARDWARE,
          this.$constants.ASSET_NON_IT,
          this.$constants.ASSET_CONSUMABLE,
          this.$constants.ASSET_FIELD_TRANSITION_HISTORY,
          this.$constants.CI_FIELD_TRANSITION_HISTORY,
          this.$constants.CMDB,
        ].indexOf(this.approver.parentResource) >= 0
      ) {
        return true
      }
      return false
    },
    parentModuleName() {
      if (
        [
          this.$constants.CI_FIELD_TRANSITION_HISTORY,
          this.$constants.CMDB,
        ].indexOf(this.approver.parentResource) >= 0
      ) {
        return this.$constants.CMDB
      }
      return this.approver.parentResource
    },
    isAssetMovement() {
      if (
        [this.$constants.ASSET_MOVEMENT].indexOf(
          this.approver.parentResource
        ) >= 0
      ) {
        return true
      }
      return false
    },
    detailComponent() {
      if (this.isTicket) {
        return TicketDetailView
      }
      if (this.isAsset) {
        return AssetDetailView
      }
      if (this.isAssetMovement) {
        return AssetMovementDetail
      }
      if (this.isKnowledgeDetail) {
        return Read
      }
      if (
        [this.$constants.PURCHASE].indexOf(this.approver.parentResource) >= 0
      ) {
        return PurchaseDetail
      }
      return undefined
    },
    // for asset module custom field
    ...FormComputed,
    ...AssetTypeComputed,
    ...CITypeComputed,
    customFields() {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      return (
        ((this.assetForm || {}).fields || []).filter(
          (f) =>
            f.attributes.model ===
              customFieldAttributesModuleName(this.parentModuleName) &&
            !f.attributes[`${typePrefix}PropertyType`] &&
            !f.attributes[`${typePrefix}ComponentType`]
        ) || []
      )
    },
    customFieldsForAll() {
      return customFieldsForAllAssetType(
        this.customFields,
        this.parentModuleName
      )
    },
    purchaseCustomFormFields() {
      if ([this.$constants.PURCHASE].indexOf(this.parentModuleName) >= 0) {
        return this[`${this.$constants.PURCHASE}Fields`]({
          exclude: [...(this.useTemplate ? [] : ['template'])],
        })
      }
      return []
    },
    priceCustomFields() {
      if ([this.$constants.PURCHASE].indexOf(this.parentModuleName) >= 0) {
        return this.purchaseCustomFormFields.filter(
          (f) => f.attributes.costField === 'true'
        )
      }
      return []
    },
    isKnowledgeDetail() {
      return [this.$constants.KNOWLEDGE].indexOf(this.parentModuleName) >= 0
    },
  },
  watch: {
    resource: {
      immediate: true,
      handler: 'getRelationModuleCount',
    },
  },
  created() {
    getApprovalSettingApi().then((data) => (this.approvalSettings = data))
    this.getApproverData()
  },
  methods: {
    getRelationModuleCount() {
      if (
        this.isTicket ||
        this.isAsset ||
        this.parentModuleName === this.$constants.PURCHASE
      ) {
        return getRelationCountApi(
          this.parentModuleName,
          this.resource.id
        ).then((data) => {
          Object.values(data).filter((value) => {
            this.relationCount = this.relationCount + value
          })
        })
      }
    },
    handleRelationCount() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    handleRelationRemoved() {
      this.relationCount = 0
      this.getRelationModuleCount()
    },
    handleRefresh() {
      this.getApproverData()
    },
    getApproverData() {
      getApproverApi(this.$route.params.id, this.isPortalLogin, {
        // ...([this.$constants.CHANGE, this.$constants.RELEASE].indexOf(
        //   this.$route.query.moduleName
        // ) >= 0
        //   ? {

        //     }
        //   : {}),
        archived: true,
      }).then(
        (data) => {
          this.approver = data
          this.loading = false
        },
        () => this.$router.replace({ name: '404' })
      )
    },
    handleNoteAdded(data) {
      return this.handleStatusChange({ ...data, ...this.pendingChanges }, true)
        .then((data) => {
          this.$refs.noteDrawer.closeForm()
          this.pendingChanges = null
          this.resolve(data)
          this.conversationRenderCount += 1
        })
        .catch(() => this.reject())
    },
    handleStatusChange(status, forceUpdate = false) {
      if (
        status.status === 'rejected' &&
        this.approvalSettings.noteRequiredToReject &&
        forceUpdate === false
      ) {
        return new Promise((resolve, reject) => {
          this.pendingChanges = status
          this.$refs.noteDrawer.openForm()
          this.resolve = resolve
          this.reject = reject
        })
      }
      return updateApproverApi(
        { id: this.approver.id, ...status },
        this.isPortalLogin
      ).then((data) => {
        this.approver = {
          ...this.approver,
          ...status,
        }
      })
    },
    selectedAssetTypes(resource) {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      if (resource[`${typePrefix}TypeId`]) {
        const collection = this[`${typePrefix}Types`]
        const assetTypeId = resource[`${typePrefix}TypeId`]
        const path = findValuePathWithItems(collection, assetTypeId)
        const rootItem = path[0]
        return {
          list: path,
          ...rootItem,
        }
      }
      return {}
    },
    customFieldForAssetType(resource) {
      const typePrefix = customFieldAttributesTypePrefix(this.parentModuleName)
      if (!resource[`${typePrefix}TypeId`]) {
        return {}
      }
      const fullPath = this.selectedAssetTypes(resource).list
      const assetTypeFields = {}
      fullPath.forEach((p) => {
        const c = this.customFields.filter(
          (f) => f.attributes[`${typePrefix}TypeId`] === String(p.id)
        )
        assetTypeFields[p.id] = c
      })
      return assetTypeFields
    },
    totalCustomFields(resource) {
      const assetTypeFields = this.customFieldForAssetType(resource)
      let f = []
      Object.keys(assetTypeFields).map((i) => {
        f = f.concat(assetTypeFields[i])
      })
      return [...f, ...this.customFieldsForAll]
    },
    isConstomFieldAvailable(resource) {
      if ((this.totalCustomFields(resource) || []).length > 0) {
        return true
      }
      return false
    },
  },
}
</script>
